import React from 'react';
import { Home } from 'lucide-react';
import EmployeeAcknowledgment from './EmployeeAcknowledgment';
import DocumentArchiveButton from './DocumentArchiveButton';
import PVSPageLayout from './PVSPageLayout';
import CalorVesselsNotice from './documents/CalorVesselsNotice';

const VesselAirTest = ({ onReturnHome }) => (
  <PVSPageLayout className="py-12 px-4">
    <div className="max-w-4xl mx-auto pvs-glass border border-pvs-accent/30 rounded-xl shadow-pvs-glow">
      <div className="border-b border-pvs-border p-6">
        <div className="flex justify-between items-center gap-4">
          <h1 className="text-2xl sm:text-3xl font-bold text-white flex-1 min-w-0">Vessel Air Test SSOW / RAMS</h1>
          <DocumentArchiveButton />
          <button
            className="group flex items-center justify-center w-12 h-12 flex-shrink-0 rounded-xl pvs-glass pvs-glass-hover transition-all duration-200"
            onClick={onReturnHome}
            title="Return to Homepage"
          >
            <Home className="w-6 h-6" stroke="url(#icon-two-tone)" />
          </button>
        </div>
      </div>
      <div className="p-8 md:p-10 lg:p-12">
        <h2 className="text-2xl font-bold text-center mb-6 text-white">Safe System of Work (SSOW) &amp; Risk Assessment</h2>
        <h3 className="text-xl font-semibold text-center mb-6 text-pvs-text-muted">Air Testing of LPG Storage Vessels (4-Bar &amp; 8-Bar)</h3>
        <table className="w-full border-collapse mb-8" style={{borderColor: '#0437F2'}}>
          <tbody>
            <tr>
              <th className="text-white text-left py-2 px-4 border font-semibold w-1/3" style={{backgroundColor: '#0437F2', borderColor: '#0437F2'}}>Document Title</th>
              <td className="py-2 px-4 border text-pvs-text-muted" style={{borderColor: '#0437F2'}}>Air Testing of LPG Storage Vessels (4-Bar &amp; 8-Bar)</td>
            </tr>
            <tr>
              <th className="text-white text-left py-2 px-4 border font-semibold" style={{backgroundColor: '#0437F2', borderColor: '#0437F2'}}>Document Reference</th>
              <td className="py-2 px-4 border text-pvs-text-muted" style={{borderColor: '#0437F2'}}>PVS-RAMS-VAT-001</td>
            </tr>
            <tr>
              <th className="text-white text-left py-2 px-4 border font-semibold" style={{backgroundColor: '#0437F2', borderColor: '#0437F2'}}>Version</th>
              <td className="py-2 px-4 border text-pvs-text-muted" style={{borderColor: '#0437F2'}}>1.1</td>
            </tr>
            <tr>
              <th className="text-white text-left py-2 px-4 border font-semibold" style={{backgroundColor: '#0437F2', borderColor: '#0437F2'}}>Date of Issue</th>
              <td className="py-2 px-4 border text-pvs-text-muted" style={{borderColor: '#0437F2'}}>17th March 2026</td>
            </tr>
            <tr>
              <th className="text-white text-left py-2 px-4 border font-semibold" style={{backgroundColor: '#0437F2', borderColor: '#0437F2'}}>Next Review Date</th>
              <td className="py-2 px-4 border text-pvs-text-muted" style={{borderColor: '#0437F2'}}>17th March 2028</td>
            </tr>
            <tr>
              <th className="text-white text-left py-2 px-4 border font-semibold" style={{backgroundColor: '#0437F2', borderColor: '#0437F2'}}>Process Owner</th>
              <td className="py-2 px-4 border text-pvs-text-muted" style={{borderColor: '#0437F2'}}>Director (Operations &amp; Quality)</td>
            </tr>
            <tr>
              <th className="text-white text-left py-2 px-4 border font-semibold" style={{backgroundColor: '#0437F2', borderColor: '#0437F2'}}>Target Audience</th>
              <td className="py-2 px-4 border text-pvs-text-muted" style={{borderColor: '#0437F2'}}>Trained Vessel Inspectors Only</td>
            </tr>
          </tbody>
        </table>

        <CalorVesselsNotice />

        <h3 className="text-2xl font-bold mb-4 text-pvs-accent">Part 1: Safe System of Work (SSOW) &amp; Method Statement</h3>

        <section className="mb-8">
          <h4 className="text-xl font-bold mb-4 text-pvs-accent">1. Purpose and Scope</h4>
          <p className="text-pvs-text-muted">
            To define the safe working method for conducting pneumatic (air) pressure tests on refurbished LPG vessels at 4-bar and 8-bar intervals. This ensures the integrity of the vessel and its fittings while protecting personnel from the hazards of stored pressure.
          </p>
        </section>

        <section className="mb-8">
          <h4 className="text-xl font-bold mb-4 text-pvs-accent">2. Personnel &amp; Competence</h4>
          <ul className="list-disc pl-5 space-y-2 text-pvs-text-muted">
            <li><strong>Authorized Personnel Only:</strong> This task must only be carried out by formally Trained and Authorized Vessel Inspectors.</li>
            <li><strong>Supervision:</strong> The test must never be left unattended at any point while the vessel is under pressure.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h4 className="text-xl font-bold mb-4 text-pvs-accent">3. Equipment Required</h4>
          <ul className="list-disc pl-5 space-y-2 text-pvs-text-muted">
            <li>Calibrated 6” dial Pressure Gauge (0-14 bar range, UKAS calibrated).</li>
            <li>Compressed air supply and rated air hoses.</li>
            <li>Whip check safety cables/lanyards.</li>
            <li>Approved Leak Detection Fluid (LDF).</li>
            <li>Temporary/retractable physical barriers and warning signage ("Danger: Pressure Testing in Progress").</li>
            <li>Venting silencer.</li>
            <li><strong>PPE Required:</strong> Safety glasses/goggles, ear protection, safety footwear, hi-vis vest, heavy-duty gloves.</li>
          </ul>
        </section>

        <section className="mb-8">
          <h4 className="text-xl font-bold mb-4 text-pvs-accent">4. Pre-Test Safety &amp; Setup</h4>
          <ol className="list-decimal pl-6 space-y-2 text-pvs-text-muted">
            <li><strong>Visual Equipment Check:</strong> Inspect all air hoses, couplings, and the 6" pressure gauge for damage, wear, or expired calibration dates. Do not use defective equipment.</li>
            <li><strong>Establish Exclusion Zone:</strong> Deploy temporary/retractable barriers at a minimum distance of 1 meter around the working ends of the vessel, accompanied by warning signage to prevent unauthorized access.</li>
            <li><strong>Whip Check Anchoring:</strong> Connect the air hose to the vessel coupling adaptor. You must attach and secure a suitable whip protection cable/lanyard from the air hose to the vessel's lifting lug to restrict movement in the event of a coupling or hose failure.</li>
            <li><strong>Gauge Installation:</strong> Carefully screw the calibrated 6” pressure gauge into the vapour valve.</li>
          </ol>
        </section>

        <section className="mb-8">
          <h4 className="text-xl font-bold mb-4 text-pvs-accent">5. Step-by-Step Testing Procedure</h4>

          <h5 className="text-lg font-semibold mb-3 mt-4 text-pvs-accent">Phase 1: Initial 4-Bar Test</h5>
          <ol className="list-decimal pl-6 space-y-2 text-pvs-text-muted">
            <li>Ensure the inspector is positioned safely, minimizing exposure to the direct vertical line of fire of fittings or valves.</li>
            <li>Gradually introduce compressed air into the vessel until the 6" gauge reads 4 bar.</li>
            <li>Isolate the air supply valve.</li>
            <li>Apply approved Leak Detection Fluid (LDF) to all connections, valves, and fittings.</li>
            <li>Visually inspect for bubbling or pressure drops on the gauge. If leaks are found, safely depressurize the vessel before attempting to tighten or adjust any fittings.</li>
          </ol>

          <h5 className="text-lg font-semibold mb-3 mt-6 text-pvs-accent">Phase 2: Final 8-Bar Test</h5>
          <ol className="list-decimal pl-6 space-y-2 text-pvs-text-muted">
            <li>If the 4-bar test is successful, re-open the air supply and gradually raise the pressure to 8 bar.</li>
            <li>Isolate the air supply valve.</li>
            <li>Re-apply LDF to all connections, valves, and fittings.</li>
            <li>Perform a final, thorough visual inspection for leaks. Record the successful test results digitally via the PVS App.</li>
          </ol>

          <h5 className="text-lg font-semibold mb-3 mt-6 text-pvs-accent">Phase 3: Safe Depressurization</h5>
          <ol className="list-decimal pl-6 space-y-2 text-pvs-text-muted">
            <li>Ensure ear protection is worn.</li>
            <li>Attach the silencer to the venting port.</li>
            <li>Slowly open the valve to vent the air pressure to the atmosphere, remaining out of the direct line of the venting air.</li>
            <li>Confirm the 6" gauge reads exactly 0 bar before disconnecting the whip checks, hoses, or removing the pressure gauge.</li>
          </ol>
        </section>

        <h3 className="text-2xl font-bold mb-4 text-pvs-accent">Part 2: Task-Specific Risk Assessment (RAMS)</h3>
        <section className="mb-8">
          <div className="overflow-x-auto rounded-lg shadow-md">
            <table className="min-w-full">
              <thead>
                <tr className="bg-pvs-card-light">
                  <th className="py-3 px-4 uppercase font-semibold text-sm text-pvs-text-muted text-left">Hazard Identified</th>
                  <th className="py-3 px-4 uppercase font-semibold text-sm text-pvs-text-muted text-left">Who Might Be Harmed?</th>
                  <th className="py-3 px-4 uppercase font-semibold text-sm text-pvs-text-muted text-left">Current Control Measures in Place</th>
                  <th className="py-3 px-4 uppercase font-semibold text-sm text-pvs-text-muted text-left">Risk Rating (With Controls)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="py-3 px-4 font-bold">Sudden release of stored pressure / Hose Whip (Coupling failure)</td>
                  <td className="py-3 px-4">Inspector, nearby yard personnel.</td>
                  <td className="py-3 px-4">Whip checks anchored to vessel lifting lugs. Regular visual inspection of hoses and ACME threads before use.</td>
                  <td className="py-3 px-4">LOW</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-bold">Ejection of fittings/valves under pressure</td>
                  <td className="py-3 px-4">Inspector, nearby yard personnel.</td>
                  <td className="py-3 px-4">Strict adherence to 2-stage testing (4-bar then 8-bar) to catch minor leaks early. Inspector instructed to stand out of the direct vertical line of fire. Threads checked with calibrated gauges prior to fitting.</td>
                  <td className="py-3 px-4">LOW</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-bold">Unauthorized access to pressure area</td>
                  <td className="py-3 px-4">Untrained PVS staff, drivers, visitors.</td>
                  <td className="py-3 px-4">Due to the low probability of shell failure but credible risk of fitting ejection, a temporary exclusion zone of a minimum of 1 meter will be established using temporary/retractable barriers. Because all pressure-retaining fittings on these specific vessels are located on the top or underneath, the theoretical 'line of fire' in the event of a failure is strictly vertical (straight up or down) and never horizontal towards yard personnel. The 1-meter barrier ensures staff remain physically aware of the active test area and keeps them out of the immediate vicinity, without unnecessarily restricting commercial yard operations. Warning signage displayed. Test must never be left unattended.</td>
                  <td className="py-3 px-4">LOW</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-bold">Noise exposure during venting</td>
                  <td className="py-3 px-4">Inspector, nearby yard personnel.</td>
                  <td className="py-3 px-4">Silencer utilized during the depressurization phase. Mandatory use of ear protection for the inspector.</td>
                  <td className="py-3 px-4">LOW</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-bold">Equipment Failure (Over-pressurization)</td>
                  <td className="py-3 px-4">Inspector.</td>
                  <td className="py-3 px-4">Use of UKAS-calibrated 6" 0-14 bar gauge. Air input closely monitored and manually isolated.</td>
                  <td className="py-3 px-4">LOW</td>
                </tr>
                <tr>
                  <td className="py-3 px-4 font-bold">Slips, Trips, and Falls</td>
                  <td className="py-3 px-4">Inspector.</td>
                  <td className="py-3 px-4">Hoses routed safely to avoid trip hazards. Spilled Leak Detection Fluid (LDF) cleaned up immediately.</td>
                  <td className="py-3 px-4">LOW</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="mb-8">
          <h3 className="text-2xl font-bold mb-4 text-pvs-accent">Document Sign-Off</h3>
          <p className="text-pvs-text-muted mb-2"><strong>Prepared By:</strong> ___________________________ <strong>Date:</strong> ____________</p>
          <p className="text-pvs-text-muted mb-4"><strong>Approved By (Director):</strong> _________________ <strong>Date:</strong> ____________</p>
          <p className="text-pvs-text-muted italic">
            All Vessel Inspectors must read, understand, and sign the PVS Training Matrix acknowledging this SSOW before conducting Air Testing activities.
          </p>
        </section>

        <EmployeeAcknowledgment
          documentName="Vessel Air Test SSOW / RAMS"
          documentReference="PVS-RAMS-VAT-001"
          processActivity="Air Testing of LPG Storage Vessels (4-Bar & 8-Bar)"
          versionNumber="1.1"
          dateOfIssue="17th March 2026"
        />
      </div>
    </div>
  </PVSPageLayout>
);

export default VesselAirTest;
